import { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Language, translations } from '../lib/translations';

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations[Language];
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const deriveLanguageFromPath = useCallback((): Language => {
    return location.pathname.startsWith('/en') ? 'en' : 'pt-BR';
  }, [location.pathname]);

  const [language, _setLanguage] = useState<Language>(deriveLanguageFromPath);

  useEffect(() => {
    _setLanguage(deriveLanguageFromPath());
  }, [deriveLanguageFromPath]);

  const setLanguage = useCallback((lang: Language) => {
    _setLanguage(lang);
    const isEnglish = lang === 'en';
    const targetPath = isEnglish ? '/en' : '/';
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  const t = useMemo(() => translations[language], [language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}